import { cn } from "@/lib/utils/cn";
import DOMPurify from "dompurify";
import parse, { domToReact, Element, type DOMNode } from "html-react-parser";
import type { ReactNode } from "react";
import { useMemo } from "react";

interface PurifiedHtmlProps {
  html?: string | null;
  fallback?: ReactNode;
  className?: string;
}

export default function PurifiedHtml({
  html,
  fallback,
  className,
}: PurifiedHtmlProps) {
  const sanitizedHtml = useMemo(() => {
    if (!html) return undefined;
    if (typeof window === "undefined") return html;
    return DOMPurify.sanitize(html, {
      USE_PROFILES: { html: true },
    });
  }, [html]);

  if (sanitizedHtml) {
    return (
      <div className={cn("prose max-w-none", className)}>
        {parse(sanitizedHtml, {
          replace: (domNode) => {
            if (!(domNode instanceof Element) || domNode.name !== "table") {
              return undefined;
            }

            return (
              <div className="border-border my-4 max-w-full overflow-x-auto rounded-lg border">
                <table className={cn("min-w-max", domNode.attribs.class)}>
                  {domToReact(domNode.children as DOMNode[])}
                </table>
              </div>
            );
          },
        })}
      </div>
    );
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  return null;
}
